import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, setDoc, getDoc, where, limit, getDocs } from 'firebase/firestore';
import { Plus, ShieldAlert, ShieldOff, Users, ShoppingBag, Image as ImageIcon, Database, Lock, X, MapPin, Phone, User as UserIcon, Calendar, Filter, ShoppingCart, ChevronRight, Video, Trash2, Save, MessageSquare, Send, Tag, Edit2, Download, ShieldCheck, Loader2 } from 'lucide-react';
import { db } from '../firebase';
import { ChatSession, ImageVariantOption, Message, NumericVariantConfig, Order, OrderStatus, Product, SelectedVariant, User, VariantGroup, Coupon } from '../types';
import { useNotification } from '../contexts/NotificationContext';
import { Button } from './Button';
import { ImageKitUpload } from './ImageKitUpload';
import { useAuth } from '../hooks/useAuth';

// PDF Libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const generateOrderInvoice = (order: Order) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const primaryColor: [number, number, number] = [234, 88, 12]; 
  const textColor: [number, number, number] = [41, 37, 36]; 
  const lightText: [number, number, number] = [120, 113, 108]; 

  // 1. Header Section - Company Info
  doc.setFontSize(24);
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.setFont("helvetica", "bold");
  doc.text('DEVINE STONE', 14, 22);

  doc.setFontSize(10);
  doc.setTextColor(textColor[0], textColor[1], textColor[2]);
  doc.setFont("helvetica", "normal");
  doc.text('Premium Gemstone Authentication', 14, 28);
  doc.setTextColor(lightText[0], lightText[1], lightText[2]);
  doc.text('Kolkata, West Bengal, India', 14, 33);
  doc.text('support@devinestone.in', 14, 38);

  // 2. Invoice Details (Right Aligned - Extra Spacing Added)
  doc.setFontSize(22);
  doc.setTextColor(textColor[0], textColor[1], textColor[2]);
  doc.text('TAX INVOICE', 196, 22, { align: 'right' });

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(`Order ID:`, 130, 30); // Shifted left to prevent overlap
  doc.setFont("helvetica", "normal");
  doc.text(order.id.substring(0, 12).toUpperCase(), 196, 30, { align: 'right' });
  
  const orderDate = new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  doc.setFont("helvetica", "bold");
  doc.text(`Date:`, 130, 36); // Shifted left
  doc.setFont("helvetica", "normal");
  doc.text(orderDate, 196, 36, { align: 'right' });

  if (order.paymentId) {
    doc.setFont("helvetica", "bold");
    doc.text(`Payment ID:`, 130, 42); // Shifted left
    doc.setFont("helvetica", "normal");
    doc.text(order.paymentId, 196, 42, { align: 'right' });
  }

  doc.setDrawColor(231, 229, 228);
  doc.setLineWidth(0.5);
  doc.line(14, 48, 196, 48);

  // 3. Customer Info Section
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.text('Shipping Address:', 14, 58);
  doc.text('Billing Address:', 105, 58); 
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(textColor[0], textColor[1], textColor[2]);

  const ship = order.shipping;
  doc.setFont("helvetica", "bold");
  doc.text(ship?.fullName || 'Customer', 14, 65);
  doc.setFont("helvetica", "normal");
  doc.text(ship?.address || 'N/A', 14, 70);
  doc.text(`${ship?.district || ''}, ${ship?.state || ''} - ${ship?.pincode || ''}`, 14, 75);
  doc.text(`Mobile: ${ship?.mobile || 'N/A'}`, 14, 80);
  if (order.alternativeMobile) doc.text(`Alt Mobile: ${order.alternativeMobile}`, 14, 85);

  const bill = order.billing || ship; 
  doc.setFont("helvetica", "bold");
  doc.text(bill?.fullName || 'Customer', 105, 65);
  doc.setFont("helvetica", "normal");
  doc.text(bill?.address || 'N/A', 105, 70);
  doc.text(`${bill?.district || ''}, ${bill?.state || ''} - ${bill?.pincode || ''}`, 105, 75);
  doc.text(`Mobile: ${bill?.mobile || ship?.mobile || 'N/A'}`, 105, 80);

  // 4. Order Items Table
  const tableColumn = ["Item Description", "Qty", "Unit Price", "Total"];
  const tableRows: any[] = [];
  let subtotal = 0;

  order.items.forEach((item: any) => {
    const itemPrice = item.unitPrice || item.currentPrice || 0;
    const lineTotal = itemPrice * item.quantity;
    subtotal += lineTotal;

    let variantText = '';
    if (item.selectedVariants && item.selectedVariants.length > 0) {
      variantText = '\n' + item.selectedVariants.map((v: any) => 
        `${v.groupName}: ${v.type === 'numeric' ? v.value + (v.unit||'') : v.optionName}`
      ).join(' | ');
    }

    const itemData = [
      item.name + variantText,
      item.quantity.toString(),
      `Rs. ${itemPrice.toLocaleString('en-IN')}`,
      `Rs. ${lineTotal.toLocaleString('en-IN')}`,
    ];
    tableRows.push(itemData);
  });

  autoTable(doc, {
    startY: 95,
    head: [tableColumn],
    body: tableRows,
    theme: 'grid',
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
    styles: { fontSize: 10, cellPadding: 6 },
    columnStyles: { 0: { cellWidth: 90 }, 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } }
  });

  // 5. Totals Section
  const finalY = (doc as any).lastAutoTable.finalY || 150;
  
  doc.setFillColor(250, 246, 240); 
  doc.rect(120, finalY + 5, 76, order.discountAmount ? 32 : 22, 'F');

  doc.setFontSize(10);
  doc.setTextColor(textColor[0], textColor[1], textColor[2]);
  
  let currentY = finalY + 12;
  
  doc.text('Subtotal:', 125, currentY);
  doc.text(`Rs. ${subtotal.toLocaleString('en-IN')}`, 190, currentY, { align: 'right' });
  currentY += 8;

  if (order.discountAmount && order.discountAmount > 0) {
    doc.setTextColor(22, 163, 74); 
    doc.text(`Discount ${order.couponCode ? `(${order.couponCode})` : ''}:`, 125, currentY);
    doc.text(`- Rs. ${order.discountAmount.toLocaleString('en-IN')}`, 190, currentY, { align: 'right' });
    currentY += 8;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.text('Grand Total:', 125, currentY);
  doc.text(`Rs. ${(order.totalAmount || 0).toLocaleString('en-IN')}`, 190, currentY, { align: 'right' });

  // 6. Footer
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(lightText[0], lightText[1], lightText[2]);
  doc.text('Thank you for shopping with Devine Stone.', 105, 275, { align: 'center' });
  doc.setFontSize(8);
  doc.text('This is a computer generated invoice and requires no physical signature.', 105, 280, { align: 'center' });

  doc.save(`Invoice_DevineStone_${order.id.substring(0, 8)}.pdf`);
};

export const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  
  const isUserAdmin = user?.isConsultant === true || (user as any)?.isAdmin === true; 
  const [isVerifying, setIsVerifying] = useState(true);

  const { showNotification } = useNotification();
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<'orders' | 'users' | 'products' | 'banner' | 'chat' | 'coupons'>('orders');
  const [orderFilter, setOrderFilter] = useState<OrderStatus | 'All'>('All');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [chatReply, setChatReply] = useState('');
  const [isSendingChatReply, setIsSendingChatReply] = useState(false);

  const [bannerType, setBannerType] = useState<'image' | 'video'>('image');
  const [bannerItems, setBannerItems] = useState<Array<{ type: 'image' | 'video'; url: string }>>([]);
  const [isSavingBanner, setIsSavingBanner] = useState(false);
  const [tempBannerUrl, setTempBannerUrl] = useState('');

  const [newProduct, setNewProduct] = useState({
    name: '', category: '', currentPrice: '', originalPrice: '', rating: '5',
    images: [] as string[], details: '', description: '', about: '', weight: '',
    origin: '', certificate: '', guarantee: '', natural: true, isNew: true,
    isTrending: false, quantityLeft: '',
  });
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [variantGroups, setVariantGroups] = useState<VariantGroup[]>([]);
  const [isSavingProduct, setIsSavingProduct] = useState(false);

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [newCoupon, setNewCoupon] = useState({
    code: '', discountType: 'percentage' as 'percentage' | 'fixed', discountValue: '',
    minOrderValue: '', isActive: true,
  });

  useEffect(() => {
    if (user) {
      setIsVerifying(false); 
    } else {
      const timer = setTimeout(() => setIsVerifying(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const stripUndefined = (value: any): any => {
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (Array.isArray(value)) {
      return value.map((v) => stripUndefined(v)).filter((v) => v !== undefined);
    }
    if (typeof value === 'object') {
      if (value.constructor !== Object) return value;
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) {
        const cleaned = stripUndefined(v);
        if (cleaned !== undefined) out[k] = cleaned;
      }
      return out;
    }
    return value;
  };

  useEffect(() => {
    if (!isUserAdmin) return;
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snapshot) => {
      setOrders(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
    });
    return () => unsub();
  }, [isUserAdmin]);

  useEffect(() => {
    if (!isUserAdmin) return;
    const q = query(collection(db, "users"));
    const unsub = onSnapshot(q, (snapshot) => {
      setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as User)));
    });
    return () => unsub();
  }, [isUserAdmin]);

  useEffect(() => {
    if (!isUserAdmin) return;
    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setProducts(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Product, 'id'>) } as Product)));
    });
    return () => unsub();
  }, [isUserAdmin]);

  useEffect(() => {
    if (!isUserAdmin) return;
    const q = query(collection(db, 'chatSessions'), orderBy('lastMessageAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setChatSessions(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChatSession, 'id'>) } as ChatSession)));
    });
    return () => unsub();
  }, [isUserAdmin]);

  useEffect(() => {
    if (!isUserAdmin || !selectedChatId) {
      setChatMessages([]);
      return;
    }
    const q = query(collection(db, 'chatSessions', selectedChatId, 'messages'), orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setChatMessages(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Message, 'id'>) } as Message)));
    });
    return () => unsub();
  }, [isUserAdmin, selectedChatId]);

  useEffect(() => {
    if (!isUserAdmin) return;
    const q = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setCoupons(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Coupon, 'id'>) } as Coupon)));
    });
    return () => unsub();
  }, [isUserAdmin]);

  useEffect(() => {
    if (!isUserAdmin) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'siteConfig', 'banners'));
        if (snap.exists()) {
          const data = snap.data() as any;
          const items = Array.isArray(data?.items) ? data.items : [];
          const normalized = items
            .map((it: any) => ({
              type: it?.type === 'video' ? 'video' : 'image',
              url: typeof it?.url === 'string' ? it.url.trim() : '',
            }))
            .filter((it: any) => it.url);
          setBannerItems(normalized);
        }
      } catch {
        // ignore
      }
    };
    load();
  }, [isUserAdmin]);

  const addBannerItem = () => {
    const url = tempBannerUrl.trim();
    if (!url) {
      showNotification('Please enter a banner URL', 'error');
      return;
    }
    setBannerItems((prev) => [...prev, { type: bannerType, url }]);
    setTempBannerUrl('');
  };

  const removeBannerItem = (index: number) => {
    setBannerItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveBanner = async () => {
    try {
      setIsSavingBanner(true);
      await setDoc(doc(db, 'siteConfig', 'banners'), {
        items: bannerItems,
        updatedAt: serverTimestamp(),
      });
      showNotification('Banner saved', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to save banner', 'error');
    } finally {
      setIsSavingBanner(false);
    }
  };

  const createProduct = async () => {
    if (!newProduct.name || !newProduct.category || !newProduct.currentPrice || !newProduct.originalPrice) {
      showNotification('Fill in all required fields', 'error');
      return;
    }
    if (newProduct.images.length === 0) {
      showNotification('At least 1 image is required', 'error');
      return;
    }

    try {
      setIsCreatingProduct(true);
      const currentPrice = Number(newProduct.currentPrice);
      const originalPrice = Number(newProduct.originalPrice);
      const rating = Number(newProduct.rating);
      const images = newProduct.images;

      if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
        showNotification('Enter a valid current price', 'error');
        return;
      }
      if (!Number.isFinite(originalPrice) || originalPrice <= 0) {
        showNotification('Enter a valid original price', 'error');
        return;
      }
      if (!Number.isFinite(rating) || rating < 0 || rating > 5) {
        showNotification('Rating must be 0-5', 'error');
        return;
      }
      if (images.length === 0) {
        showNotification('At least 1 image is required', 'error');
        return;
      }
      const payload = stripUndefined({
        name: newProduct.name.trim(),
        category: newProduct.category.trim(),
        currentPrice,
        originalPrice,
        rating,
        images,
        details: newProduct.details.trim(),
        description: newProduct.description.trim(),
        about: newProduct.about.trim(),
        weight: newProduct.weight.trim(),
        origin: newProduct.origin.trim(),
        certificate: newProduct.certificate.trim(),
        guarantee: newProduct.guarantee.trim(),
        natural: Boolean(newProduct.natural),
        isNew: Boolean(newProduct.isNew),
        isTrending: Boolean(newProduct.isTrending),
        quantityLeft: Number(newProduct.quantityLeft) || 0,
        variants: variantGroups,
        createdAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'products'), payload);
      showNotification('Product added', 'success');
      setNewProduct({
        name: '',
        category: '',
        currentPrice: '',
        originalPrice: '',
        rating: '5',
        images: [],
        details: '',
        description: '',
        about: '',
        weight: '',
        origin: '',
        certificate: '',
        guarantee: '',
        natural: true,
        isNew: true,
        isTrending: false,
        quantityLeft: '',
      });
      setVariantGroups([]);
    } catch (e: any) {
      showNotification(e?.message || 'Failed to add product', 'error');
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    return products.find((p) => p.id === selectedProductId) || null;
  }, [products, selectedProductId]);

  const beginEditProduct = (p: Product) => {
    setSelectedProductId(p.id);
    setShowAddProduct(false);
    setNewProduct({
      name: p.name || '',
      category: p.category || '',
      currentPrice: String(p.currentPrice ?? ''),
      originalPrice: String(p.originalPrice ?? ''),
      rating: String(p.rating ?? '5'),
      images: Array.isArray(p.images) ? p.images : [],
      details: p.details || '',
      description: p.description || '',
      about: p.about || '',
      weight: p.weight || '',
      origin: p.origin || '',
      certificate: p.certificate || '',
      guarantee: p.guarantee || '',
      natural: Boolean(p.natural),
      isNew: Boolean(p.isNew),
      isTrending: Boolean(p.isTrending),
      quantityLeft: String(p.quantityLeft ?? ''),
    });
    setVariantGroups(Array.isArray(p.variants) ? p.variants : []);
  };

  const resetProductForm = () => {
    setSelectedProductId(null);
    setNewProduct({
      name: '',
      category: '',
      currentPrice: '',
      originalPrice: '',
      rating: '5',
      images: [],
      details: '',
      description: '',
      about: '',
      weight: '',
      origin: '',
      certificate: '',
      guarantee: '',
      natural: true,
      isNew: true,
      isTrending: false,
      quantityLeft: '',
    });
    setVariantGroups([]);
  };

  const saveProductEdits = async () => {
    if (!selectedProductId) return;
    if (!newProduct.name.trim() || !newProduct.category.trim()) {
      showNotification('Name and category are required', 'error');
      return;
    }

    const currentPrice = Number(newProduct.currentPrice);
    const originalPrice = Number(newProduct.originalPrice);
    const rating = Number(newProduct.rating);
    const images = newProduct.images;
    const quantityLeft = Number(newProduct.quantityLeft);

    if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
      showNotification('Enter a valid current price', 'error');
      return;
    }
    if (!Number.isFinite(originalPrice) || originalPrice <= 0) {
      showNotification('Enter a valid original price', 'error');
      return;
    }
    if (!Number.isFinite(rating) || rating < 0 || rating > 5) {
      showNotification('Rating must be 0-5', 'error');
      return;
    }
    if (images.length === 0) {
      showNotification('At least 1 image URL is required', 'error');
      return;
    }

    try {
      setIsSavingProduct(true);
      const payload = stripUndefined({
        name: newProduct.name.trim(),
        category: newProduct.category.trim(),
        currentPrice,
        originalPrice,
        rating,
        images,
        details: newProduct.details.trim(),
        description: newProduct.description.trim(),
        about: newProduct.about.trim(),
        weight: newProduct.weight.trim(),
        origin: newProduct.origin.trim(),
        certificate: newProduct.certificate.trim(),
        guarantee: newProduct.guarantee.trim(),
        natural: Boolean(newProduct.natural),
        isNew: Boolean(newProduct.isNew),
        isTrending: Boolean(newProduct.isTrending),
        quantityLeft: Number.isFinite(quantityLeft) ? quantityLeft : 0,
        variants: variantGroups,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'products', selectedProductId), payload);
      showNotification('Product updated', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to update product', 'error');
    } finally {
      setIsSavingProduct(false);
    }
  };

  const deleteProduct = async () => {
    if (!selectedProductId) return;
    try {
      await deleteDoc(doc(db, 'products', selectedProductId));
      showNotification('Product deleted', 'success');
      resetProductForm();
      setSelectedProductId(null);
    } catch (e: any) {
      showNotification(e?.message || 'Failed to delete product', 'error');
    }
  };

  const addVariantGroup = () => {
    const id = `vg_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setVariantGroups((prev) => [
      ...prev,
      {
        id,
        name: 'Variant',
        type: 'numeric',
        numeric: { min: 1, max: 10, step: 0.5, unit: '', stepPrice: 0 },
      },
    ]);
  };

  const removeVariantGroup = (groupId: string) => {
    setVariantGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const updateVariantGroup = (groupId: string, patch: Partial<VariantGroup>) => {
    setVariantGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));
  };

  const updateNumericVariant = (groupId: string, patch: Partial<NumericVariantConfig>) => {
    setVariantGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          type: 'numeric',
          numeric: {
            min: Number(g.numeric?.min ?? 0),
            max: Number(g.numeric?.max ?? 0),
            step: Number(g.numeric?.step ?? 0.5),
            unit: g.numeric?.unit ?? '',
            stepPrice: Number(g.numeric?.stepPrice ?? 0),
            ...patch,
          },
          options: undefined,
        };
      })
    );
  };

  const addImageOption = (groupId: string) => {
    const id = `vo_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setVariantGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const next: ImageVariantOption[] = Array.isArray(g.options) ? g.options : [];
        return {
          ...g,
          type: 'image',
          options: [...next, { id, name: 'Option', imageUrl: '', price: 0 }],
          numeric: undefined,
        };
      })
    );
  };

  const updateImageOption = (groupId: string, optionId: string, patch: Partial<ImageVariantOption>) => {
    setVariantGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const options = Array.isArray(g.options) ? g.options : [];
        return {
          ...g,
          type: 'image',
          options: options.map((o) => (o.id === optionId ? { ...o, ...patch } : o)),
          numeric: undefined,
        };
      })
    );
  };

  const removeImageOption = (groupId: string, optionId: string) => {
    setVariantGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const options = Array.isArray(g.options) ? g.options : [];
        return {
          ...g,
          type: 'image',
          options: options.filter((o) => o.id !== optionId),
          numeric: undefined,
        };
      })
    );
  };

  const deleteOrder = async (orderId: string) => {
    if (!window.confirm('Delete this order permanently?')) return;
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      showNotification('Order deleted', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to delete order', 'error');
    }
  };

  // Chat Administration Controls
  const clearChatMessages = async (chatId: string) => {
    if (!window.confirm('Clear all messages in this chat?')) return;
    try {
      const snap = await getDocs(collection(db, "chatSessions", chatId, "messages"));
      await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
      await updateDoc(doc(db, "chatSessions", chatId), { lastMessageAt: serverTimestamp() });
      showNotification('Chat cleared', 'success');
    } catch (e: any) {
       showNotification(e.message || 'Failed to clear chat', 'error');
    }
  };

  const deleteChatSession = async (chatId: string) => {
    if (!window.confirm('Delete this chat entirely? This cannot be undone.')) return;
    try {
      const snap = await getDocs(collection(db, "chatSessions", chatId, "messages"));
      await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
      await deleteDoc(doc(db, "chatSessions", chatId));
      if (selectedChatId === chatId) setSelectedChatId(null);
      showNotification('Chat deleted completely', 'success');
    } catch (e: any) {
       showNotification(e.message || 'Failed to delete chat', 'error');
    }
  };

  const toggleUserBan = async (user: User) => {
    const newBanStatus = !user.isBanned;
    if (!window.confirm(`${newBanStatus ? 'Ban' : 'Unban'} this user?`)) return;
    try {
      await updateDoc(doc(db, 'users', user.email || user.mobile), {
        isBanned: newBanStatus
      });
      showNotification(`User ${newBanStatus ? 'banned' : 'unbanned'}`, 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to update ban status', 'error');
    }
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus) => {
    await updateDoc(doc(db, "orders", orderId), { status });
    showNotification(`Order status set to ${status}`, "success");
  };

  const updateArrivingDate = async (orderId: string, date: string) => {
    await updateDoc(doc(db, "orders", orderId), { arrivingDate: date, status: 'Arriving' });
    showNotification("Arrival date updated", "success");
  };

  const filteredOrders = useMemo(() => {
    if (orderFilter === 'All') return orders;
    return orders.filter(o => o.status === orderFilter);
  }, [orders, orderFilter]);

  const orderCounts = useMemo(() => {
    return {
      'All': orders.length,
      'Just Ordered': orders.filter(o => o.status === 'Just Ordered').length,
      'Dispatched': orders.filter(o => o.status === 'Dispatched').length,
      'Arriving': orders.filter(o => o.status === 'Arriving').length,
      'Delivered': orders.filter(o => o.status === 'Delivered').length,
    };
  }, [orders]);

  const sortedChatSessions = useMemo(() => {
    const list = [...chatSessions];
    list.sort((a, b) => {
      const aNeeds = Boolean(a.waitingForReply) || a.lastSenderRole === 'customer';
      const bNeeds = Boolean(b.waitingForReply) || b.lastSenderRole === 'customer';
      if (aNeeds !== bNeeds) return aNeeds ? -1 : 1;

      const aTs = (a.lastMessageAt as any)?.toMillis ? (a.lastMessageAt as any).toMillis() : 0;
      const bTs = (b.lastMessageAt as any)?.toMillis ? (b.lastMessageAt as any).toMillis() : 0;
      return bTs - aTs;
    });
    return list;
  }, [chatSessions]);

  const selectedChat = useMemo(() => {
    if (!selectedChatId) return null;
    return chatSessions.find((s) => s.id === selectedChatId) || null;
  }, [chatSessions, selectedChatId]);

  const sendAdminReply = async () => {
    if (!selectedChatId || !selectedChat) return;
    const text = chatReply.trim();
    if (!text) return;
    try {
      setIsSendingChatReply(true);
      await addDoc(collection(db, 'chatSessions', selectedChatId, 'messages'), {
        text,
        senderMobile: selectedChat.consultantMobile || 'ADMIN',
        senderName: selectedChat.consultantName || 'Admin',
        senderRole: 'consultant',
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, 'chatSessions', selectedChatId), {
        userQuotaRemaining: 5,
        waitingForReply: false,
        lastMessageAt: serverTimestamp(),
        lastSenderRole: 'consultant',
      });
      setChatReply('');
      showNotification('Reply sent', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to send reply', 'error');
    } finally {
      setIsSendingChatReply(false);
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim()) {
      showNotification('Coupon code is required', 'error');
      return;
    }

    const discountValue = Number(newCoupon.discountValue);
    const minOrderValue = newCoupon.minOrderValue ? Number(newCoupon.minOrderValue) : undefined;

    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      showNotification('Invalid discount value', 'error');
      return;
    }

    if (newCoupon.discountType === 'percentage' && discountValue > 100) {
      showNotification('Percentage discount cannot exceed 100%', 'error');
      return;
    }

    if (minOrderValue && (!Number.isFinite(minOrderValue) || minOrderValue <= 0)) {
      showNotification('Invalid minimum order value', 'error');
      return;
    }

    try {
      const couponData = {
        code: newCoupon.code.toUpperCase().trim(),
        discountType: newCoupon.discountType,
        discountValue,
        minOrderValue,
        isActive: newCoupon.isActive,
        createdAt: serverTimestamp(),
      };

      if (editingCoupon) {
        await updateDoc(doc(db, 'coupons', editingCoupon.id), {
          ...couponData,
          updatedAt: serverTimestamp(),
        });
        showNotification('Coupon updated', 'success');
        setEditingCoupon(null);
      } else {
        await addDoc(collection(db, 'coupons'), couponData);
        showNotification('Coupon created', 'success');
      }

      resetCouponForm();
      setShowAddCoupon(false);
    } catch (e: any) {
      showNotification(e?.message || 'Failed to save coupon', 'error');
    }
  };

  const deleteCoupon = async (couponId: string) => {
    try {
      await deleteDoc(doc(db, 'coupons', couponId));
      showNotification('Coupon deleted', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to delete coupon', 'error');
    }
  };

  const editCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setNewCoupon({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: String(coupon.discountValue),
      minOrderValue: coupon.minOrderValue ? String(coupon.minOrderValue) : '',
      isActive: coupon.isActive,
    });
    setShowAddCoupon(true);
  };

  const resetCouponForm = () => {
    setNewCoupon({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      minOrderValue: '',
      isActive: true,
    });
    setEditingCoupon(null);
  };

  // Show loading spinner while determining if user is an admin
  if (isVerifying && !isUserAdmin) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mb-4"></div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Verifying Clearance...</p>
      </div>
    );
  }

  // Redirect if grace period is over and user is NOT an admin
  if (!isVerifying && !isUserAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 pb-20">
      {/* Sleek Admin Header */}
      <div className="bg-stone-900 text-white p-6 border-b border-stone-800">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-stone-800 p-3 rounded-xl border border-stone-700">
              <Database size={24} className="text-orange-500" />
            </div>
            <div>
              <h1 className="text-xl font-serif font-bold tracking-widest uppercase">Admin Console</h1>
              <p className="text-[10px] text-stone-400 font-mono tracking-widest uppercase mt-1">Devine Stone • Secure Management</p>
            </div>
          </div>
          
          <div className="text-right bg-stone-800/50 px-4 py-2 rounded-xl border border-stone-700">
            <p className="text-[10px] text-stone-400 uppercase tracking-widest mb-0.5">Active Session</p>
            <p className="text-sm font-medium text-white">{user?.email || user?.mobile}</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-stone-200 p-4 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto">
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => setActiveTab('orders')} 
              className={`px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 ${activeTab === 'orders' ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            >
              <ShoppingBag size={14} /> Orders
            </button>
            <button 
              onClick={() => setActiveTab('users')} 
              className={`px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 ${activeTab === 'users' ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            >
              <Users size={14} /> Users
            </button>
            <button 
              onClick={() => setActiveTab('products')} 
              className={`px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 ${activeTab === 'products' ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            >
              <ShoppingCart size={14} /> Products
            </button>
            <button 
              onClick={() => setActiveTab('banner')} 
              className={`px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 ${activeTab === 'banner' ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            >
              <ImageIcon size={14} /> Banner
            </button>
            <button 
              onClick={() => setActiveTab('chat')} 
              className={`px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 ${activeTab === 'chat' ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            >
              <MessageSquare size={14} /> Chat
            </button>
            <button 
              onClick={() => setActiveTab('coupons')} 
              className={`px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 ${activeTab === 'coupons' ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            >
              <Tag size={14} /> Coupons
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content Area - Safe JSX Blocks */}
      <div className="container mx-auto p-4 lg:p-8">
        
        {/* ---- CHAT TAB ---- */}
        {activeTab === 'chat' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-sm">
              <div className="p-5 bg-stone-50 border-b border-stone-200">
                <p className="text-[10px] font-black uppercase tracking-widest text-stone-700">Chat Sessions</p>
                <p className="text-[9px] font-bold text-stone-500 mt-1">{sortedChatSessions.length} Threads Active</p>
              </div>
              <div className="divide-y divide-stone-100">
                {sortedChatSessions.map((s) => {
                  const needs = Boolean(s.waitingForReply) || s.lastSenderRole === 'customer';
                  const isActive = s.id === selectedChatId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedChatId(s.id)}
                      className={`w-full text-left p-4 hover:bg-orange-50/50 transition-colors ${isActive ? 'bg-orange-50/80 border-l-2 border-orange-500' : 'bg-white border-l-2 border-transparent'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-wide text-stone-900 truncate">{s.customerName || s.customerMobile || s.customerEmail}</p>
                          <p className="text-[9px] font-mono text-stone-500 truncate mt-0.5">{s.customerEmail || s.customerMobile}</p>
                        </div>
                        {needs && (
                          <span className="px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-widest bg-red-100 text-red-700">Action Req</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-2 bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-sm">
              {!selectedChat ? (
                <div className="p-10 text-center text-stone-400 text-[10px] font-bold uppercase tracking-widest h-[70vh] flex items-center justify-center">
                  Select a conversation to begin
                </div>
              ) : (
                <div className="flex flex-col h-[70vh]">
                  <div className="p-5 bg-stone-50 border-b border-stone-200 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-widest text-stone-900">{selectedChat.customerName || selectedChat.customerMobile}</p>
                      <p className="text-[10px] font-mono text-stone-500 mt-0.5">{selectedChat.customerEmail || selectedChat.customerMobile}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => clearChatMessages(selectedChat.id)} 
                        className="px-4 py-2 bg-white border border-stone-200 text-stone-600 rounded-xl text-[9px] font-bold uppercase tracking-widest hover:bg-stone-100 transition-colors shadow-sm"
                      >
                        Clear Messages
                      </button>
                      <button 
                        onClick={() => deleteChatSession(selectedChat.id)} 
                        className="px-4 py-2 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[9px] font-bold uppercase tracking-widest hover:bg-red-100 transition-colors shadow-sm"
                      >
                        Delete Thread
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-stone-50/50">
                    {chatMessages.map((m) => {
                      const mine = m.senderRole === 'consultant'; 
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`${mine ? 'bg-orange-600 text-white rounded-br-sm' : 'bg-white text-stone-900 border border-stone-200 rounded-bl-sm'} max-w-[75%] rounded-2xl px-5 py-3 text-[12px] font-medium shadow-sm leading-relaxed`}>
                            {m.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="p-5 border-t border-stone-200 bg-white flex gap-3">
                    <input
                      value={chatReply}
                      onChange={(e) => setChatReply(e.target.value)}
                      className="flex-1 px-5 py-3 rounded-xl border border-stone-300 bg-stone-50 text-sm font-medium text-stone-900 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all"
                      placeholder="Type your response..."
                      onKeyDown={(e) => e.key === 'Enter' && sendAdminReply()}
                    />
                    <button
                      type="button"
                      onClick={sendAdminReply}
                      disabled={isSendingChatReply || !chatReply.trim()}
                      className="px-6 py-3 rounded-xl bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-60 flex items-center justify-center shadow-lg hover:bg-orange-700 transition-colors"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- PRODUCTS TAB ---- */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-serif font-bold flex items-center gap-3 text-stone-900">
                <ShoppingCart className="text-orange-600" /> Products Catalog
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowAddProduct((s) => !s);
                    setSelectedProductId(null);
                    if (!showAddProduct) resetProductForm();
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-stone-900 text-white text-[10px] font-bold uppercase tracking-widest shadow-md hover:bg-stone-800 transition-colors"
                >
                  <Plus size={14} /> {showAddProduct ? 'Close Editor' : 'Add New Product'}
                </button>
                {selectedProductId && (
                  <button
                    onClick={() => {
                      setSelectedProductId(null);
                      resetProductForm();
                    }}
                    className="px-5 py-2.5 rounded-xl border border-stone-300 bg-white text-stone-700 text-[10px] font-bold uppercase tracking-widest hover:bg-stone-50"
                  >
                    Clear Form
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-3xl p-5 shadow-sm">
              {products.length === 0 ? (
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 text-center py-10">Inventory is empty</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => beginEditProduct(p)}
                      className={`text-left p-4 rounded-2xl border transition-all ${selectedProductId === p.id ? 'border-orange-500 bg-orange-50/50 shadow-md ring-2 ring-orange-100' : 'border-stone-200 hover:border-stone-300 hover:shadow-sm'}`}
                    >
                      <div className="flex items-center gap-4">
                        <img src={p.images?.[0] || ''} className="w-14 h-14 rounded-xl object-cover border border-stone-200 bg-stone-50" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-stone-900 truncate mb-0.5">{p.name}</p>
                          <p className="text-[9px] font-bold text-stone-500 uppercase tracking-widest truncate">{p.category}</p>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between">
                        <p className="text-sm font-black text-stone-900">₹{Number(p.currentPrice || 0).toLocaleString('en-IN')}</p>
                        <p className="text-[9px] font-bold text-stone-500 uppercase bg-stone-100 px-2.5 py-1 rounded-md">{Array.isArray(p.variants) ? p.variants.length : 0} options</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Editor Form */}
            {(showAddProduct || selectedProductId) && (
              <div className="bg-white border border-stone-200 rounded-3xl p-6 lg:p-8 shadow-xl animate-[fadeIn_0.3s_ease-out]">
                <h3 className="text-lg font-serif font-bold text-stone-900 mb-6">{selectedProductId ? 'Edit Product' : 'Create Product'}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Name</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.name} onChange={(e) => setNewProduct((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Category</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.category} onChange={(e) => setNewProduct((p) => ({ ...p, category: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Current Price (₹)</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.currentPrice} onChange={(e) => setNewProduct((p) => ({ ...p, currentPrice: e.target.value.replace(/[^0-9.]/g, '') }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Original Price (₹)</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.originalPrice} onChange={(e) => setNewProduct((p) => ({ ...p, originalPrice: e.target.value.replace(/[^0-9.]/g, '') }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Stock Quantity</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" type="number" value={newProduct.quantityLeft} onChange={(e) => setNewProduct((p) => ({ ...p, quantityLeft: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Rating (0-5)</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.rating} onChange={(e) => setNewProduct((p) => ({ ...p, rating: e.target.value.replace(/[^0-9.]/g, '') }))} />
                  </div>

                  <div className="md:col-span-2 flex flex-wrap items-center gap-6 bg-stone-50 p-5 rounded-xl border border-stone-200">
                    <label className="flex items-center gap-2 text-sm font-bold text-stone-700 cursor-pointer">
                      <input type="checkbox" checked={newProduct.natural} onChange={(e) => setNewProduct((p) => ({ ...p, natural: e.target.checked }))} className="w-4 h-4 text-orange-600 rounded border-stone-400" />
                      Natural Gemstone
                    </label>
                    <label className="flex items-center gap-2 text-sm font-bold text-stone-700 cursor-pointer">
                      <input type="checkbox" checked={newProduct.isNew} onChange={(e) => setNewProduct((p) => ({ ...p, isNew: e.target.checked }))} className="w-4 h-4 text-orange-600 rounded border-stone-400" />
                      New Arrival Tag
                    </label>
                    <label className="flex items-center gap-2 text-sm font-bold text-stone-700 cursor-pointer">
                      <input type="checkbox" checked={newProduct.isTrending} onChange={(e) => setNewProduct((p) => ({ ...p, isTrending: e.target.checked }))} className="w-4 h-4 text-orange-600 rounded border-stone-400" />
                      Trending Tag
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Product Images</p>
                    <ImageKitUpload value={newProduct.images} onChange={(urls) => setNewProduct((p) => ({ ...p, images: urls }))} maxFiles={5} accept="images" maxSizeMB={1} />
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Short Subtitle / Details</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.details} onChange={(e) => setNewProduct((p) => ({ ...p, details: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Full Description</p>
                    <textarea className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none min-h-[120px]" value={newProduct.description} onChange={(e) => setNewProduct((p) => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">About the Stone</p>
                    <textarea className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none min-h-[100px]" value={newProduct.about} onChange={(e) => setNewProduct((p) => ({ ...p, about: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Weight</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.weight} onChange={(e) => setNewProduct((p) => ({ ...p, weight: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Origin</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.origin} onChange={(e) => setNewProduct((p) => ({ ...p, origin: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Certificate Info</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.certificate} onChange={(e) => setNewProduct((p) => ({ ...p, certificate: e.target.value }))} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Guarantee Info</p>
                    <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium focus:border-orange-500 outline-none" value={newProduct.guarantee} onChange={(e) => setNewProduct((p) => ({ ...p, guarantee: e.target.value }))} />
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-stone-200">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-stone-900">Variants Configuration</h3>
                    <Button variant="outline" onClick={addVariantGroup} className="rounded-xl border-stone-300 text-stone-700 hover:bg-stone-50">
                      <Plus size={14} className="mr-2" /> Add Variant
                    </Button>
                  </div>

                  {variantGroups.length === 0 ? (
                    <p className="text-xs font-medium text-stone-500 bg-stone-50 p-6 rounded-2xl text-center border border-stone-200 border-dashed">No variants configured. Product will be sold as a single standard item.</p>
                  ) : (
                    <div className="space-y-6">
                      {variantGroups.map((g) => (
                        <div key={g.id} className="p-6 rounded-3xl border border-stone-200 bg-stone-50">
                          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5">
                            <div className="flex-1">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2 ml-1">Variant Name</p>
                              <input className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm font-medium bg-white focus:border-orange-500 outline-none" value={g.name} onChange={(e) => updateVariantGroup(g.id, { name: e.target.value })} placeholder="e.g. Color, Weight, Size" />
                            </div>
                            <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-stone-200 shadow-sm">
                              <button onClick={() => updateVariantGroup(g.id, { type: 'numeric', numeric: g.numeric || { min: 1, max: 10, step: 0.5, unit: '', stepPrice: 0 }, options: undefined })} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${g.type === 'numeric' ? 'bg-stone-900 text-white' : 'bg-transparent text-stone-500 hover:bg-stone-50'}`}>Numeric</button>
                              <button onClick={() => updateVariantGroup(g.id, { type: 'image', options: Array.isArray(g.options) ? g.options : [], numeric: undefined })} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${g.type === 'image' ? 'bg-stone-900 text-white' : 'bg-transparent text-stone-500 hover:bg-stone-50'}`}>Image/List</button>
                              <div className="w-px h-6 bg-stone-200 mx-1"></div>
                              <button onClick={() => removeVariantGroup(g.id)} className="p-2 text-stone-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"><Trash2 size={16} /></button>
                            </div>
                          </div>

                          {g.type === 'numeric' && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 p-5 bg-white rounded-2xl border border-stone-200 shadow-sm">
                              <div><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Min</p><input className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm font-medium" value={String(g.numeric?.min ?? '')} onChange={(e) => updateNumericVariant(g.id, { min: Number(e.target.value) || 0 })} /></div>
                              <div><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Max</p><input className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm font-medium" value={String(g.numeric?.max ?? '')} onChange={(e) => updateNumericVariant(g.id, { max: Number(e.target.value) || 0 })} /></div>
                              <div><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Step</p><input className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm font-medium" value={String(g.numeric?.step ?? '')} onChange={(e) => updateNumericVariant(g.id, { step: Number(e.target.value) || 0.5 })} /></div>
                              <div><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Unit</p><input className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm font-medium" value={String(g.numeric?.unit ?? '')} onChange={(e) => updateNumericVariant(g.id, { unit: e.target.value })} placeholder="e.g. rati" /></div>
                              <div><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Price/Step (₹)</p><input className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm font-medium" value={String(g.numeric?.stepPrice ?? '')} onChange={(e) => updateNumericVariant(g.id, { stepPrice: Number(e.target.value) || 0 })} /></div>
                            </div>
                          )}

                          {g.type === 'image' && (
                            <div className="mt-6">
                              <div className="flex items-center justify-between mb-4 border-b border-stone-200 pb-3">
                                <p className="text-xs font-bold uppercase tracking-widest text-stone-700">List Options</p>
                                <Button variant="outline" onClick={() => addImageOption(g.id)} className="rounded-lg h-9 text-[10px] px-4"><Plus size={14} className="mr-1" /> Add Choice</Button>
                              </div>
                              {(g.options || []).length === 0 ? (
                                <p className="text-xs font-medium text-stone-400 bg-white p-4 rounded-xl text-center border border-stone-200">No choices added yet.</p>
                              ) : (
                                <div className="space-y-4">
                                  {(g.options || []).map((o) => (
                                    <div key={o.id} className="grid grid-cols-1 md:grid-cols-5 gap-5 p-5 bg-white rounded-2xl border border-stone-200 shadow-sm items-center">
                                      <div className="md:col-span-1"><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Label</p><input className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-medium" value={o.name} onChange={(e) => updateImageOption(g.id, o.id, { name: e.target.value })} /></div>
                                      <div className="md:col-span-2"><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Image (Optional)</p><ImageKitUpload value={o.imageUrl ? [o.imageUrl] : []} onChange={(urls) => updateImageOption(g.id, o.id, { imageUrl: urls[0] || '' })} maxFiles={1} accept="images" maxSizeMB={1} /></div>
                                      <div className="md:col-span-1"><p className="text-[9px] font-bold uppercase tracking-widest text-stone-500 mb-1 ml-1">Added Price (+₹)</p><input className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-medium" value={String(o.price ?? 0)} onChange={(e) => updateImageOption(g.id, o.id, { price: Number(e.target.value) || 0 })} /></div>
                                      <div className="md:col-span-1 flex justify-end items-center pt-4"><button onClick={() => removeImageOption(g.id, o.id)} className="text-[10px] font-bold uppercase tracking-widest text-red-500 hover:text-red-700 bg-red-50 px-4 py-2 rounded-lg flex items-center gap-2"><Trash2 size={14} /> Remove</button></div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-10 pt-8 border-t border-stone-200">
                  {selectedProductId ? (
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button onClick={saveProductEdits} className="rounded-xl flex-1 shadow-lg bg-stone-900 text-white" disabled={isSavingProduct}>
                        {isSavingProduct ? <><Loader2 size={16} className="animate-spin mr-2" /> Saving...</> : <><Save size={16} className="mr-2" /> Save Product Updates</>}
                      </Button>
                      <Button onClick={deleteProduct} className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" variant="outline">
                        <Trash2 size={16} className="mr-2" /> Delete Product
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={createProduct} className="rounded-xl w-full py-4 text-sm shadow-lg bg-orange-600 text-white" disabled={isCreatingProduct}>
                      {isCreatingProduct ? <><Loader2 size={18} className="animate-spin mr-2" /> Creating...</> : <><Plus size={18} className="mr-2" /> Publish New Product</>}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---- BANNER TAB ---- */}
        {activeTab === 'banner' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-serif font-bold flex items-center gap-3 text-stone-900">
                <ImageIcon className="text-orange-600" /> Storefront Banners
              </h2>
            </div>

            <div className="bg-white border border-stone-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-8">
              <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200 flex gap-4 items-start">
                <div className="p-2.5 bg-white rounded-xl shadow-sm text-orange-600 shrink-0"><ImageIcon size={24} /></div>
                <div>
                  <p className="text-sm font-bold uppercase tracking-widest text-stone-900 mb-1.5">Banner Configuration</p>
                  <p className="text-xs font-medium text-stone-600 leading-relaxed">
                    Upload images or videos to display at the top of the store. Recommended dimensions: <span className="font-bold text-stone-900 bg-white px-2 py-1 rounded-md border border-stone-200 shadow-sm mx-1">1600 × 600 pixels</span>.
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <button onClick={() => setBannerType('image')} className={`px-6 py-3 rounded-xl border text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all ${bannerType === 'image' ? 'bg-stone-900 border-stone-900 text-white shadow-md' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                    <ImageIcon size={16} /> Image
                  </button>
                  <button onClick={() => setBannerType('video')} className={`px-6 py-3 rounded-xl border text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all ${bannerType === 'video' ? 'bg-stone-900 border-stone-900 text-white shadow-md' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                    <Video size={16} /> Video
                  </button>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-4">Select {bannerType === 'video' ? 'Video File' : 'Image File'}</p>
                  <ImageKitUpload
                    value={tempBannerUrl ? [tempBannerUrl] : []}
                    onChange={(urls) => setTempBannerUrl(urls[0] || '')}
                    maxFiles={1}
                    accept={bannerType === 'video' ? 'videos' : 'images'}
                    maxSizeMB={1}
                  />
                  
                  <div className="mt-6 flex justify-end">
                    <Button onClick={addBannerItem} className="rounded-xl px-8" disabled={!tempBannerUrl}>
                      <Plus size={16} className="mr-2" /> Add to Queue
                    </Button>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-stone-200">
                <h3 className="text-sm font-bold uppercase tracking-wider text-stone-900 mb-5 flex items-center justify-between">
                  Live Banners 
                  <span className="bg-stone-100 text-stone-700 px-3 py-1.5 rounded-lg text-xs font-black">{bannerItems.length} Active</span>
                </h3>
                
                {bannerItems.length === 0 ? (
                  <div className="text-center py-12 bg-stone-50 rounded-2xl border border-dashed border-stone-300">
                    <p className="text-sm font-bold uppercase tracking-widest text-stone-400">No banners are currently active.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bannerItems.map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-5 p-4 rounded-2xl border border-stone-200 bg-white shadow-sm hover:shadow-md hover:border-orange-200 transition-all group">
                        <div className="flex items-center gap-5 min-w-0">
                          <div className="w-16 h-16 bg-stone-100 rounded-xl flex items-center justify-center text-stone-400 shrink-0 overflow-hidden shadow-inner border border-stone-200">
                            {it.type === 'image' ? (
                              <img src={it.url} alt="banner" className="w-full h-full object-cover" />
                            ) : (
                              <Video size={24} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <span className="inline-block px-2.5 py-1 bg-stone-100 text-stone-600 rounded-md text-[9px] font-black uppercase tracking-widest mb-1.5">{it.type}</span>
                            <p className="text-xs font-mono text-stone-500 truncate max-w-lg">{it.url}</p>
                          </div>
                        </div>
                        <button onClick={() => removeBannerItem(idx)} className="p-3 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors shrink-0">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-8 border-t border-stone-200 flex justify-end">
                <Button onClick={saveBanner} className="rounded-xl px-10 py-4 text-sm shadow-xl bg-orange-600 text-white" disabled={isSavingBanner}>
                  {isSavingBanner ? <><Loader2 size={18} className="animate-spin mr-2" /> Syncing...</> : <><Save size={18} className="mr-2" /> Save & Publish Live</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ---- ORDERS TAB ---- */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <h2 className="text-xl font-serif font-bold flex items-center gap-3 text-stone-900">
                <ShoppingBag className="text-orange-600" /> Order Registry
              </h2>
              
              <div className="flex flex-wrap gap-2 justify-center bg-white p-1.5 rounded-2xl border border-stone-200 shadow-sm">
                {(['All', 'Just Ordered', 'Dispatched', 'Arriving', 'Delivered'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setOrderFilter(f)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider flex items-center gap-2 transition-all ${
                      orderFilter === f 
                        ? 'bg-stone-900 text-white shadow-sm' 
                        : 'bg-transparent text-stone-500 hover:bg-stone-50'
                    }`}
                  >
                    {f} <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black ${orderFilter === f ? 'bg-stone-700 text-white' : 'bg-stone-100 text-stone-500'}`}>{orderCounts[f]}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              {filteredOrders.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-3xl border border-dashed border-stone-300 shadow-sm">
                  <Filter className="mx-auto text-stone-300 mb-5" size={48} />
                  <p className="text-stone-500 uppercase tracking-widest font-bold text-sm">No orders found in this category.</p>
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <div key={order.id} className={`bg-white border rounded-3xl overflow-hidden transition-all duration-300 ${expandedOrderId === order.id ? 'border-orange-300 shadow-xl ring-4 ring-orange-50' : 'border-stone-200 shadow-sm hover:border-stone-300'}`}>
                    
                    {/* Collapsed Order Header */}
                    <div 
                      onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                      className={`p-5 lg:p-6 cursor-pointer flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-colors ${expandedOrderId === order.id ? 'bg-orange-50/30' : 'bg-white hover:bg-stone-50'}`}
                    >
                      <div className="flex items-center gap-5 w-full sm:w-auto">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors shrink-0 ${expandedOrderId === order.id ? 'bg-orange-600 text-white shadow-md' : 'bg-stone-100 text-stone-500'}`}>
                          <ShoppingBag size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <p className="text-sm font-mono font-bold text-stone-900 truncate">{order.id.substring(0, 12).toUpperCase()}</p>
                            <span className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest whitespace-nowrap ${
                              order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                              order.status === 'Arriving' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'Dispatched' ? 'bg-orange-100 text-orange-700' :
                              'bg-stone-100 text-stone-600'
                            }`}>
                              {order.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <p className="text-[11px] font-bold text-stone-500 flex items-center gap-1.5"><UserIcon size={12}/> {order.shipping?.fullName || 'Customer'}</p>
                            <div className="w-1 h-1 rounded-full bg-stone-300 hidden sm:block"></div>
                            <p className="text-[11px] font-black text-stone-900">₹{(order.totalAmount || 0).toLocaleString('en-IN')}</p>
                            <div className="w-1 h-1 rounded-full bg-stone-300 hidden sm:block"></div>
                            <p className="text-[10px] font-bold uppercase text-stone-400">{order.items.reduce((acc, it) => acc + it.quantity, 0)} Items</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 sm:gap-4 self-end sm:self-auto w-full sm:w-auto justify-end border-t sm:border-0 border-stone-100 pt-3 sm:pt-0">
                        <div className="text-right hidden md:block mr-2">
                           <p className="text-[9px] font-bold uppercase text-stone-400 tracking-widest mb-1">Order Date</p>
                           <p className="text-xs font-bold text-stone-700">{new Date(order.createdAt).toLocaleDateString('en-IN', {day: 'numeric', month: 'short', year: 'numeric'})}</p>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); generateOrderInvoice(order); }}
                          className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-stone-600 hover:text-white bg-stone-100 hover:bg-stone-900 rounded-xl transition-all flex items-center gap-2 shadow-sm"
                          title="Generate & Download PDF Invoice"
                        >
                          <Download size={14} /> PDF
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); deleteOrder(order.id); }}
                          className="p-2.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                          title="Delete Order"
                        >
                          <Trash2 size={18} />
                        </button>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 ml-1 ${expandedOrderId === order.id ? 'rotate-180 bg-orange-200 text-orange-800' : 'bg-stone-100 text-stone-400'}`}>
                          <ChevronRight size={18} />
                        </div>
                      </div>
                    </div>

                    {/* Expanded Order Details */}
                    <div className={`grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-stone-200 bg-stone-50/50 transition-all duration-500 ${expandedOrderId === order.id ? 'opacity-100 max-h-[3000px] border-t border-stone-200' : 'opacity-0 max-h-0 overflow-hidden'}`}>
                      
                      {/* Products List & Totals */}
                      <div className="p-6 lg:p-8 space-y-6">
                        <p className="text-xs font-black uppercase text-stone-900 tracking-widest flex items-center gap-2 mb-4">
                          <ShoppingCart size={16} className="text-orange-600" /> Acquired Items
                        </p>
                        <div className="space-y-3">
                          {order.items.map((item, i) => (
                            <div key={i} className="bg-white p-4 rounded-2xl border border-stone-200 flex gap-4 items-center shadow-sm">
                              <img src={item.images?.[0] || 'https://via.placeholder.com/150'} className="w-14 h-14 rounded-xl object-cover border border-stone-100 bg-stone-50 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-stone-900 truncate mb-1">{item.name}</p>
                                <div className="flex items-center justify-between mb-1.5">
                                   <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 bg-stone-100 px-2 py-0.5 rounded">Qty: {item.quantity}</span>
                                   <span className="text-xs font-black text-stone-900">₹{((item.unitPrice || item.currentPrice || 0) * item.quantity).toLocaleString('en-IN')}</span>
                                </div>
                                {item.selectedVariants && item.selectedVariants.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {item.selectedVariants.map((v: any, vi: number) => (
                                      <span key={vi} className="text-[9px] font-bold uppercase tracking-wider bg-orange-50 px-2 py-0.5 rounded border border-orange-100 text-orange-800">
                                        {v.groupName}: {v.type === 'numeric' ? `${v.value}${v.unit}` : v.optionName}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-5 border-t border-stone-200 space-y-3">
                           <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-stone-500">
                             <span>Subtotal</span>
                             <span>₹{(order.totalAmount + (order.discountAmount || 0)).toLocaleString('en-IN')}</span>
                           </div>
                           {(order.discountAmount && order.discountAmount > 0) ? (
                             <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-green-600">
                               <span>Promo {order.couponCode && `(${order.couponCode})`}</span>
                               <span>-₹{order.discountAmount.toLocaleString('en-IN')}</span>
                             </div>
                           ) : null}
                           <div className="flex justify-between items-end pt-3 border-t border-stone-200">
                             <span className="text-sm font-black uppercase text-stone-900 tracking-wider">Final Settlement</span>
                             <span className="text-2xl font-black text-orange-600 tracking-tighter">₹{order.totalAmount.toLocaleString('en-IN')}</span>
                           </div>
                        </div>
                      </div>

                      {/* Delivery Detail Simplified */}
                      <div className="p-6 lg:p-8 space-y-6">
                        <p className="text-xs font-black uppercase text-stone-900 tracking-widest flex items-center gap-2">
                          <MapPin size={16} className="text-orange-600" /> Delivery Protocol
                        </p>
                        
                        <div className="bg-white p-5 rounded-3xl border border-stone-200 shadow-sm relative overflow-hidden ring-4 ring-stone-50">
                          <div className="absolute top-0 left-0 w-1.5 h-full bg-orange-500"></div>
                          <div className="pl-3">
                            <p className="text-[10px] font-black uppercase text-stone-400 mb-3 tracking-widest border-b border-stone-100 pb-2">Shipping Destination</p>
                            <p className="font-bold text-base text-stone-900 mb-1">{order.shipping?.fullName}</p>
                            <p className="text-sm font-mono font-medium text-stone-600 mb-3 flex items-center gap-2"><Phone size={14}/> {order.shipping?.mobile}</p>
                            <p className="text-sm font-medium text-stone-700 leading-relaxed bg-stone-50 p-4 rounded-xl border border-stone-100">
                              {order.shipping?.address}<br/>
                              {order.shipping?.district}, {order.shipping?.state}<br/>
                              <span className="font-bold block mt-2 pt-2 border-t border-stone-200 text-stone-900">PINCODE: {order.shipping?.pincode}</span>
                            </p>
                          </div>
                        </div>

                        <div className="bg-orange-50/50 border border-orange-100 p-4 rounded-2xl flex items-start gap-3">
                          <ShieldCheck size={20} className="text-orange-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-stone-900 mb-1">Billing Identity Locked</p>
                            <p className="text-xs font-medium text-stone-600 leading-relaxed">Full dual-address details (Billing & Shipping) are securely rendered on the official PDF Invoice. Click Download PDF above to view.</p>
                          </div>
                        </div>
                      </div>

                      {/* Controls & Transactions */}
                      <div className="p-6 lg:p-8 space-y-6 bg-white lg:bg-transparent">
                        <div className="bg-stone-900 p-6 rounded-3xl shadow-xl">
                          <p className="text-[10px] font-black uppercase text-stone-400 tracking-widest mb-5 border-b border-stone-700 pb-3">Admin Status Override</p>
                          
                          <div className="space-y-5">
                            <div>
                              <label className="text-[10px] font-bold uppercase text-stone-400 mb-2 block tracking-widest">Fulfillment Stage</label>
                              <select 
                                value={order.status} 
                                onChange={(e) => updateOrderStatus(order.id, e.target.value as OrderStatus)} 
                                className="w-full bg-stone-800 border border-stone-700 text-white rounded-xl px-4 py-3 text-sm font-bold outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 shadow-inner cursor-pointer transition-all"
                              >
                                <option value="Just Ordered">Just Ordered (Pending)</option>
                                <option value="Dispatched">Dispatched (In Transit)</option>
                                <option value="Arriving">Arriving (Out for Delivery)</option>
                                <option value="Delivered">Delivered (Completed)</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold uppercase text-stone-400 mb-2 block tracking-widest">ETA Projection</label>
                              <input 
                                type="date" 
                                value={order.arrivingDate || ''} 
                                onChange={(e) => updateArrivingDate(order.id, e.target.value)} 
                                className="w-full bg-stone-800 border border-stone-700 text-white rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 shadow-inner cursor-pointer transition-all [color-scheme:dark]" 
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-stone-50 p-5 rounded-2xl border border-stone-200">
                          <p className="text-[10px] font-black uppercase text-stone-400 mb-3 tracking-widest">Digital Footprint</p>
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-stone-500 flex justify-between items-center">Gateway: <span className="font-bold text-stone-900 bg-white px-2 py-0.5 rounded border border-stone-200">{order.paymentMethod || 'Razorpay'}</span></p>
                            {order.paymentId && (
                              <p className="text-xs font-medium text-stone-500 flex justify-between items-center">Auth ID: <span className="font-mono font-bold text-stone-900 bg-white px-2 py-0.5 rounded border border-stone-200">{order.paymentId}</span></p>
                            )}
                            <div className="pt-3 mt-3 border-t border-stone-200">
                              <p className="text-[10px] font-bold uppercase text-stone-400 mb-1 tracking-widest">Registered User</p>
                              <p className="text-sm font-bold text-stone-900">{order.userName || 'Guest Checkout'}</p>
                              <p className="text-[10px] font-mono text-stone-500 mt-0.5">{order.userEmail || order.userId}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ---- USERS TAB ---- */}
        {activeTab === 'users' && (
          <div className="space-y-6">
             <div className="flex justify-between items-center">
               <h2 className="text-xl font-serif font-bold flex items-center gap-3 text-stone-900">
                 <Users className="text-orange-600" /> Customer Directory
               </h2>
               <span className="text-[10px] font-black uppercase tracking-widest bg-stone-900 text-white px-4 py-2 rounded-xl shadow-md">{users.length} Profiles</span>
             </div>
             
             <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="p-5 font-black uppercase text-[10px] text-stone-500 tracking-widest">Identity</th>
                      <th className="p-5 font-black uppercase text-[10px] text-stone-500 tracking-widest text-center">Active Cart Status</th>
                      <th className="p-5 font-black uppercase text-[10px] text-stone-500 tracking-widest text-right">Security Control</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {users.map((user) => (
                      <React.Fragment key={user.email || user.mobile}>
                        <tr 
                          onClick={() => setExpandedUserId(expandedUserId === (user.email || user.mobile) ? null : (user.email || user.mobile))}
                          className={`transition-colors align-middle cursor-pointer ${expandedUserId === (user.email || user.mobile) ? 'bg-stone-50' : 'hover:bg-stone-50/50'}`}
                        >
                          <td className="p-5">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-stone-400 overflow-hidden shadow-inner">
                                {user.picture ? <img src={user.picture} className="w-full h-full object-cover" /> : <UserIcon size={24} />}
                              </div>
                              <div>
                                <p className="font-bold text-stone-900 text-sm mb-0.5">{(user as any).name || user.fullName || 'Anonymous'}</p>
                                <p className="text-[10px] font-mono text-stone-500">{user.email || user.mobile}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-5 text-center">
                            {user.cart && user.cart.length > 0 ? (
                              <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-3 py-1.5 rounded-lg border border-orange-200">
                                <ShoppingCart size={14} />
                                <span className="text-[10px] font-black uppercase tracking-widest">{user.cart.length} Items</span>
                                <ChevronRight size={14} className={`transition-transform duration-300 ml-1 ${expandedUserId === (user.email || user.mobile) ? 'rotate-90' : ''}`} />
                              </div>
                            ) : (
                              <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest bg-stone-50 px-3 py-1.5 rounded-lg border border-stone-200">Empty</span>
                            )}
                          </td>
                          <td className="p-5 text-right">
                            <div className="flex items-center justify-end gap-4">
                              <div className="text-right">
                                <span className={`inline-block px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest ${user.isConsultant ? 'bg-orange-600 text-white shadow-sm' : 'bg-stone-100 text-stone-500'}`}>
                                  {user.isConsultant ? 'Admin' : 'Customer'}
                                </span>
                              </div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); toggleUserBan(user); }}
                                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${user.isBanned ? 'bg-stone-900 text-white shadow-md' : 'bg-white border border-stone-200 text-stone-600 hover:border-red-200 hover:text-red-600 hover:bg-red-50'}`}
                                title={user.isBanned ? 'Lift Security Ban' : 'Ban Account'}
                              >
                                {user.isBanned ? <><ShieldOff size={14}/> Lift Ban</> : <><ShieldAlert size={14}/> Suspend</>}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedUserId === (user.email || user.mobile) && user.cart && user.cart.length > 0 && (
                          <tr className="bg-stone-100/50">
                            <td colSpan={3} className="p-6 border-t border-stone-200">
                              <div className="bg-white border border-stone-200 rounded-3xl p-6 shadow-sm">
                                <p className="text-[10px] font-black uppercase text-stone-900 mb-5 tracking-widest flex items-center gap-2">
                                  <Database size={14} className="text-orange-600"/> Items inside User's Cart
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                  {user.cart.map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-4 p-4 bg-stone-50 rounded-2xl border border-stone-200">
                                      {item.images && item.images.length > 0 ? (
                                        <img src={item.images[0]} className="w-14 h-14 rounded-xl object-cover border border-stone-200 bg-white shadow-sm" />
                                      ) : (
                                        <div className="w-14 h-14 rounded-xl bg-stone-100 flex items-center justify-center text-stone-300 border border-stone-200">
                                          <ShoppingBag size={20} />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-stone-900 truncate mb-1">{item.name}</p>
                                        <p className="text-[10px] text-stone-600 font-bold uppercase tracking-widest mb-2">
                                          ₹{(((typeof (item as any).unitPrice === 'number' && Number.isFinite((item as any).unitPrice)) ? (item as any).unitPrice : item.currentPrice) * item.quantity).toLocaleString('en-IN')} <span className="text-orange-600 ml-1">x{item.quantity}</span>
                                        </p>
                                        {Array.isArray((item as any).selectedVariants) && (item as any).selectedVariants.length > 0 && (
                                          <div className="flex flex-wrap gap-1">
                                            {((item as any).selectedVariants as SelectedVariant[]).map((v, vi) => (
                                              <p key={vi} className="text-[8px] font-black uppercase tracking-wider text-stone-600 bg-white px-2 py-0.5 rounded border border-stone-200">
                                                {v.groupName}: {v.type === 'numeric' ? `${v.value}${v.unit}` : v.optionName}
                                              </p>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ---- COUPONS TAB ---- */}
        {activeTab === 'coupons' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-serif font-bold flex items-center gap-3 text-stone-900">
                <Tag className="text-orange-600" /> Promotion Engine
              </h2>
              <button
                onClick={() => {
                  setShowAddCoupon(true);
                  resetCouponForm();
                }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-stone-900 text-white text-[10px] font-bold uppercase tracking-widest shadow-md hover:bg-stone-800 transition-colors"
              >
                <Plus size={14} /> Generate Code
              </button>
            </div>

            {showAddCoupon && (
              <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 shadow-xl relative animate-[slideUp_0.3s_ease-out]">
                <button onClick={() => setShowAddCoupon(false)} className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 bg-stone-50 hover:bg-stone-100 p-2 rounded-full transition-colors">
                   <X size={20} />
                </button>
                <h3 className="text-xl font-serif font-bold mb-8 flex items-center gap-3 text-stone-900">
                  <Tag className="text-orange-600"/> {editingCoupon ? 'Update Configuration' : 'New Promotion'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase text-stone-500 tracking-widest ml-1">Access Code</label>
                    <input
                      type="text"
                      value={newCoupon.code}
                      onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                      placeholder="e.g. LAUNCH20"
                      className="w-full px-4 py-3.5 border border-stone-200 rounded-xl focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-sm font-bold font-mono outline-none uppercase bg-stone-50 focus:bg-white transition-all"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase text-stone-500 tracking-widest ml-1">Reduction Type</label>
                    <select
                      value={newCoupon.discountType}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discountType: e.target.value as 'percentage' | 'fixed' })}
                      className="w-full px-4 py-3.5 border border-stone-200 rounded-xl focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-sm font-bold outline-none bg-stone-50 focus:bg-white cursor-pointer transition-all"
                    >
                      <option value="percentage">Percentage Based (%)</option>
                      <option value="fixed">Flat Amount (₹)</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase text-stone-500 tracking-widest ml-1">
                      Reduction Value
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-bold">
                        {newCoupon.discountType === 'percentage' ? '%' : '₹'}
                      </span>
                      <input
                        type="number"
                        value={newCoupon.discountValue}
                        onChange={(e) => setNewCoupon({ ...newCoupon, discountValue: e.target.value })}
                        placeholder={newCoupon.discountType === 'percentage' ? '15' : '500'}
                        className="w-full pl-10 pr-4 py-3.5 border border-stone-200 rounded-xl focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-sm font-bold outline-none bg-stone-50 focus:bg-white transition-all"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase text-stone-500 tracking-widest ml-1">Order Threshold (Min Value)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-bold">₹</span>
                      <input
                        type="number"
                        value={newCoupon.minOrderValue}
                        onChange={(e) => setNewCoupon({ ...newCoupon, minOrderValue: e.target.value })}
                        placeholder="Leave blank for no minimum"
                        className="w-full pl-10 pr-4 py-3.5 border border-stone-200 rounded-xl focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-sm font-bold outline-none bg-stone-50 focus:bg-white transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-5 bg-stone-50 rounded-2xl border border-stone-200 mb-8">
                  <label className="flex items-center gap-4 cursor-pointer w-full">
                    <input
                      type="checkbox"
                      checked={newCoupon.isActive}
                      onChange={(e) => setNewCoupon({ ...newCoupon, isActive: e.target.checked })}
                      className="w-6 h-6 text-orange-600 rounded-md border-stone-300 focus:ring-orange-500"
                    />
                    <div>
                      <span className="text-sm font-bold text-stone-900 block">Status: Live</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Uncheck to instantly pause this promotion across the store</span>
                    </div>
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={createCoupon}
                    className="px-8 py-4 bg-orange-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:shadow-xl shadow-orange-500/20 hover:bg-orange-700 transition-all flex items-center justify-center gap-2 flex-1"
                  >
                    <Save size={16} />
                    {editingCoupon ? 'Apply Configuration' : 'Deploy to Store'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddCoupon(false);
                      resetCouponForm();
                    }}
                    className="px-8 py-4 border border-stone-300 text-stone-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-stone-50 transition-all flex items-center justify-center"
                  >
                    Discard Changes
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-sm">
              {coupons.length === 0 ? (
                <div className="text-center py-20 bg-stone-50">
                   <Tag size={40} className="mx-auto text-stone-300 mb-4" />
                   <p className="text-[10px] font-black uppercase tracking-widest text-stone-500">Promotional Engine Empty</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-900 text-stone-400">
                      <tr>
                        <th className="text-left p-5 text-[10px] font-black uppercase tracking-widest">Identifier</th>
                        <th className="text-left p-5 text-[10px] font-black uppercase tracking-widest">Logic</th>
                        <th className="text-left p-5 text-[10px] font-black uppercase tracking-widest">Threshold</th>
                        <th className="text-left p-5 text-[10px] font-black uppercase tracking-widest">Telemetry</th>
                        <th className="text-right p-5 text-[10px] font-black uppercase tracking-widest">Admin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {coupons.map((coupon) => (
                        <tr key={coupon.id} className="hover:bg-stone-50 transition-colors">
                          <td className="p-5">
                            <span className="text-sm font-black text-stone-900 bg-stone-100 border border-stone-200 px-3 py-1.5 rounded-lg font-mono inline-block">
                              {coupon.code}
                            </span>
                          </td>
                          <td className="p-5">
                            <div className="flex flex-col">
                              <span className="text-sm font-black text-orange-600">
                                {coupon.discountType === 'percentage' ? `${coupon.discountValue}% OFF` : `₹${coupon.discountValue} OFF`}
                              </span>
                              <span className="text-[9px] font-bold text-stone-500 uppercase tracking-widest mt-0.5">
                                {coupon.discountType}
                              </span>
                            </div>
                          </td>
                          <td className="p-5">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-stone-600 bg-white border border-stone-200 px-3 py-1.5 rounded-md">
                              {coupon.minOrderValue ? `₹${coupon.minOrderValue} MIN` : 'NO LIMIT'}
                            </span>
                          </td>
                          <td className="p-5">
                            <span className={`px-3 py-1.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${
                              coupon.isActive 
                                ? 'bg-green-100 text-green-700 border-green-200' 
                                : 'bg-red-50 text-red-600 border-red-200'
                            }`}>
                              {coupon.isActive ? 'Active' : 'Paused'}
                            </span>
                          </td>
                          <td className="p-5">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => editCoupon(coupon)}
                                className="p-2.5 text-stone-500 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-colors border border-transparent hover:border-orange-200"
                                title="Edit Logic"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => deleteCoupon(coupon.id)}
                                className="p-2.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                                title="Delete Permanently"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
